import type React from "react";
import { DropOptions, NodeModel, isAncestor } from "@minoru/react-dnd-treeview";
import { useEffect } from "react";
import { FileProperties } from "../../../models/FileProperties";
import type { DelayCodeLabels } from "../../../delayCodeLabels";
import { useReorderDelayCodesMutation } from "../../../../hooks/mutations/useDelayCodeMutations";

interface Props {
  delay: NodeModel<FileProperties>[];
  selectedNodes: NodeModel<FileProperties>[];
  isCtrlPressing: boolean;
  setIsCtrlPressing: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedNodes: React.Dispatch<
    React.SetStateAction<NodeModel<FileProperties>[]>
  >;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  onDelayUpdateEnd: (
    result: { ok: true; dropTargetId: number } | { ok: false; message: string },
  ) => void;
  onDelayUpdateStart?: () => void;
  labels: DelayCodeLabels;
}

export const useDelayActions = ({
  delay,
  selectedNodes,
  setSelectedNodes,
  isCtrlPressing,
  setIsDragging,
  setIsCtrlPressing,
  onDelayUpdateEnd,
  onDelayUpdateStart = () => {},
  labels,
}: Props) => {
  const reorderMutation = useReorderDelayCodesMutation();
  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key.toLowerCase() === "escape") {
        setSelectedNodes([]);
      } else if (e.ctrlKey || e.metaKey) {
        setIsCtrlPressing(true);
      }
    };

    const handleKeyUp = (e: any) => {
      if (e.key.toLowerCase() === "control" || e.key.toLowerCase() === "meta") {
        setIsCtrlPressing(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleDrop = (
    _: NodeModel<FileProperties>[],
    options: DropOptions<FileProperties>,
  ) => {
    const { dropTargetId } = options;

    if (
      selectedNodes.some((selectedNode) =>
        isAncestor(delay, selectedNode.id, dropTargetId),
      )
    ) {
      onDelayUpdateEnd({
        ok: false,
        message: `${labels.singular} cannot be moved to a descendant`,
      });
      return;
    }

    if (dropTargetId === 0) {
      onDelayUpdateEnd({
        ok: false,
        message: `${labels.singular} cannot be moved outside its tree`,
      });
      return;
    }

    onDelayUpdateStart();

    reorderMutation.mutate(
      {
        delayCodeId: selectedNodes.map((cn) => cn.id),
        aboveDelayCodeId: dropTargetId as number,
      },
      {
        onSuccess: () => {
          setSelectedNodes([]);
          onDelayUpdateEnd({
            ok: true,
            dropTargetId: dropTargetId as number,
          });
        },
        onError: (err) => {
          onDelayUpdateEnd({
            ok: false,
            message:
              err instanceof Error
                ? err.message
                : `Error reordering ${labels.plural.toLowerCase()}`,
          });
        },
      },
    );
  };

  const handleSingleSelect = (node: NodeModel<FileProperties>) => {
    setSelectedNodes([node]);
  };

  const handleMultiSelect = (clickedNode: NodeModel<FileProperties>) => {
    const selectedIds = selectedNodes.map((n) => n.id);

    if (selectedIds.includes(clickedNode.id)) {
      return;
    }
    if (
      selectedIds.some((selectedId) =>
        isAncestor(delay, selectedId, clickedNode.id),
      )
    ) {
      return;
    }

    let updateNodes = [...selectedNodes];

    updateNodes = updateNodes.filter((selectedNode) => {
      return !isAncestor(delay, clickedNode.id, selectedNode.id);
    });

    updateNodes = [...updateNodes, clickedNode];
    setSelectedNodes(updateNodes);
  };

  const handleCtrlClick = (
    e: React.MouseEvent,
    node: NodeModel<FileProperties>,
  ) => {
    if (e.ctrlKey || e.metaKey) {
      handleMultiSelect(node);
    } else {
      handleSingleSelect(node);
    }
  };

  const handleDragStart = (node: NodeModel<FileProperties>) => {
    const isSelectedNode = selectedNodes.some((n) => n.id === node.id);
    setIsDragging(true);

    if (!isCtrlPressing && isSelectedNode) {
      return;
    }

    if (!isCtrlPressing) {
      setSelectedNodes([node]);
      return;
    }

    if (!selectedNodes.some((n) => n.id === node.id)) {
      setSelectedNodes([...selectedNodes, node]);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setIsCtrlPressing(false);
  };
  return { handleDrop, handleCtrlClick, handleDragEnd, handleDragStart };
};
